import { Icon, Loading, SearchInput } from "@alm/flow-ds/components";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import api from "lib/api";
import _ from "lodash";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface Role {
	users: string[];
	managers: string[];
	name: string;
	description: string;
	type: "system" | "standard";
}

export default () => {
	const { t } = useTranslation();

	const { _isMounted } = useCheckMountedStatus();

	const [roles, setRoles] = useState<[]>([]);
	const [loading, setLoading] = useState(false);
	const [searchBox, setSearchBox] = useState("");
	const [searchValue, setSearchValue] = useState("");
	const [currentRoles, setCurrentRoles] = useState([]);

	/**
	 * Handles change in search box
	 */
	const onChangeEvent = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (event.target.value !== "") {
				setSearchBox(event.target.value);
			} else {
				setSearchBox(event.target.value);
				setSearchValue(event.target.value);
			}
		},
		[]
	);

	/**
	 * Update currentRoles if searchValue has changed
	 */
	useEffect(() => {
		if (searchValue.trim()) {
			const trimVal = searchValue.trim();
			setCurrentRoles(
				roles.filter((role: Role) => role.name.includes(trimVal))
			);
		} else setCurrentRoles(roles);
	}, [roles, searchValue]);

	/**
	 * Gets all available roles on mount
	 */
	useEffect(() => {
		// Only update state when the component is mounted
		const getRoles = async () => {
			if (_isMounted.current) setLoading(true);
			try {
				const res = await api.roles.getAllRoles();
				if (_isMounted.current) {
					setRoles(res);
				}
			} catch (e) {
				console.error(e);
			}
			if (_isMounted.current) setLoading(false);
		};

		getRoles();
	}, [_isMounted]);

	if (loading) return <Loading className="elem__loader--md" />;

	return (
		<>
			<div className="admin__title">
				<h2>{t("main.admin.headings.roles")}</h2>
				<Link
					className="btn btn-primary admin__addRole-btn"
					to="/admin/roles/add-role">
					{t("main.admin.labels.addRole")}
				</Link>
			</div>
			<div className="search-activities">
				<div className="search-bar">
					<SearchInput
						label={t("main.text.search")}
						onSearch={() => setSearchValue(searchBox)}
						onChange={onChangeEvent}
						value={searchBox}
					/>
				</div>
			</div>
			{currentRoles.length > 0 ? (
				<div className="admin__role-table">
					<div className="admin__role-heading admin__role-item">
						<div className="admin__role-name admin__role-item__column">
							{t("main.admin.headings.role")}
						</div>
						<div className="admin__role-type admin__role-item__column">
							{t("main.admin.headings.type")}
						</div>
						<div className="admin__role-members admin__role-item__column">
							{t("main.admin.headings.members")}
						</div>
						<div className="admin__role-managers admin__role-item__column">
							{t("main.admin.headings.managers")}
						</div>
						<div className="admin__role-desc admin__role-item__column">
							{t("main.admin.headings.desc")}
						</div>
					</div>
					{currentRoles.map((role: Role, index: number) => {
						const { name, description, users, managers, type } =
							role;
						return (
							<div
								className="admin__role-item"
								key={`${role.name}-${index}`}>
								<div className="admin__role-name admin__role-item__column">
									<div>
										<Link
											to={`/admin/roles/edit-role/${name}`}
											className="me-2">
											{name}
										</Link>
										{type === "system" && (
											<Icon type="lock-alt" weight="s" />
										)}
									</div>
								</div>
								<div className="admin__role-type admin__role-item__column">
									{`${_.upperFirst(type)} ${t(
										"main.admin.text.user"
									)}`}
								</div>
								<div className="admin__role-members admin__role-item__column">
									{t("main.admin.labels.membersCount", {
										count: users.length,
									})}
								</div>
								<div className="admin__role-managers admin__role-item__column">
									{t("main.admin.labels.managersCount", {
										count: managers.length,
									})}
								</div>
								<div className="admin__role-desc admin__role-item__column">
									{description}
								</div>
							</div>
						);
					})}
				</div>
			) : (
				<div className="mt-5 text-center">
					{t("main.admin.text.noRolesFound")}
				</div>
			)}
		</>
	);
};
